// PersonalCalendar.js - Calendrier personnel type Outlook
import { API_BASE } from '../../config';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  Home, Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight,
  Clock, MapPin, Users, X, Edit2, Trash2, Save
} from 'lucide-react';

// Local YYYY-MM-DD to avoid UTC shift (events showing wrong day)
const toLocalDateString = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Hook pour récupérer les headers d'authentification
const useAuthHeaders = () => {
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };
  return { getAuthHeaders };
};

// Hook pour récupérer les infos utilisateur
const useUserInfo = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }
        
        const response = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
          }
        }
      } catch (error) {
        console.error('Erreur récupération infos utilisateur:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserInfo();
  }, []);

  return { user, loading };
};

// Composant principal
const PersonalCalendar = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'week' ou 'month'
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  const { user } = useUserInfo();
  const { getAuthHeaders } = useAuthHeaders();

  // Formulaire événement
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    location: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    isAllDay: false,
    color: '#3B82F6'
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadEvents();
  }, [currentDate, viewMode]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const startDate = getStartOfView();
      const endDate = getEndOfView();
      
      console.log('📅 Chargement événements:', { startDate, endDate, viewMode });
      
      const response = await fetch(
        `${API_BASE}/api/personal-calendar?startDate=${startDate}&endDate=${endDate}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('📥 Événements reçus:', data);
        if (data.success) {
          console.log(`✅ ${data.events?.length || 0} événement(s) chargé(s)`);
          if (data.events && data.events.length > 0) {
            data.events.forEach((e, i) => {
              console.log(`[Calendar] Raw API event ${i} BEFORE normalization:`, { startDate: e.startDate, endDate: e.endDate });
            });
          }
          const normalized = (data.events || []).map(e => {
            const rawStart = e.startDate != null ? String(e.startDate) : '';
            const rawEnd = e.endDate != null ? String(e.endDate) : '';
            const startDate = rawStart
              ? (rawStart.includes('T') ? toLocalDateString(new Date(rawStart)) : rawStart.substring(0, 10))
              : e.startDate;
            const endDate = rawEnd
              ? (rawEnd.includes('T') ? toLocalDateString(new Date(rawEnd)) : rawEnd.substring(0, 10))
              : e.endDate;
            const isAllDay = e.isAllDay === true;
            const startTime = isAllDay && (e.startTime == null || e.startTime === '') ? '00:00' : (e.startTime || '00:00');
            const endTime = isAllDay && (e.endTime == null || e.endTime === '') ? '23:59' : (e.endTime || '23:59');
            return { ...e, startDate, endDate, startTime, endTime, isAllDay };
          });
          setEvents(normalized);
        } else {
          console.warn('⚠️ Réponse non réussie:', data);
        }
      } else {
        console.error('❌ Erreur HTTP:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Détails erreur:', errorData);
      }
    } catch (error) {
      console.error('❌ Erreur chargement événements:', error);
    } finally {
      setLoading(false);
    }
  };

  // Toujours charger le mois entier pour la date courante : Month et Week affichent les mêmes événements.
  const getStartOfView = () => {
    const date = new Date(currentDate);
    date.setDate(1);
    return toLocalDateString(date);
  };

  const getEndOfView = () => {
    const date = new Date(currentDate);
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return toLocalDateString(date);
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const openEventModal = (date = null, time = null, event = null) => {
    if (event) {
      // Mode édition
      setSelectedEvent(event);
      setEventForm({
        title: event.title || '',
        description: event.description || '',
        location: event.location || '',
        startDate: event.startDate || '',
        startTime: event.startTime || '',
        endDate: event.endDate || '',
        endTime: event.endTime || '',
        isAllDay: event.isAllDay || false,
        color: event.color || '#3B82F6'
      });
    } else {
      // Mode création — no default date; user picks start/end date, event shows on that date on calendar
      setSelectedEvent(null);
      const eventTime = time || '09:00';
      setEventForm({
        title: '',
        description: '',
        location: '',
        startDate: '',
        startTime: eventTime,
        endDate: '',
        endTime: addTime(eventTime, 1),
        isAllDay: false,
        color: '#3B82F6'
      });
    }
    setSelectedDate(date);
    setSelectedTime(time);
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setEventForm({
      title: '',
      description: '',
      location: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      isAllDay: false,
      color: '#3B82F6'
    });
  };

  const addTime = (time, hours) => {
    const [h, m] = time.split(':').map(Number);
    const newH = (h + hours) % 24;
    return `${String(newH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const saveEvent = async () => {
    try {
      const url = selectedEvent
        ? `${API_BASE}/api/personal-calendar/${selectedEvent.id}`
        : `${API_BASE}/api/personal-calendar`;
      
      const method = selectedEvent ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: eventForm.title,
          description: eventForm.description,
          location: eventForm.location,
          startDate: eventForm.startDate,
          startTime: eventForm.isAllDay ? '00:00' : eventForm.startTime,
          endDate: eventForm.endDate,
          endTime: eventForm.isAllDay ? '23:59' : eventForm.endTime,
          isAllDay: eventForm.isAllDay,
          color: eventForm.color
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Événement sauvegardé:', data);
        closeEventModal();
        // Attendre un peu pour que la base de données soit à jour
        setTimeout(() => {
          loadEvents();
        }, 500);
      } else {
        const data = await response.json();
        console.error('❌ Erreur sauvegarde:', data);
        alert(t('calendar.error_saving').replace('{{error}}', data.error || t('calendar.error_saving_unknown')));
      }
    } catch (error) {
      console.error('Erreur sauvegarde événement:', error);
      alert(t('calendar.error_saving_generic'));
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm(t('calendar.delete_confirm'))) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/personal-calendar/${eventId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        loadEvents();
      } else {
        alert(t('calendar.error_deleting'));
      }
    } catch (error) {
      console.error('Erreur suppression événement:', error);
      alert(t('calendar.error_deleting'));
    }
  };

  const getDaysInWeek = () => {
    const start = new Date(currentDate);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1);
    start.setDate(diff);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    const days = [];
    const current = new Date(startDate);
    while (current <= lastDay || days.length < 42) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
      if (days.length >= 42) break;
    }
    return days;
  };

  // Date-only comparison (no new Date() parsing) + inclusive end: show event on start..end inclusive
  const getEventsForDate = (date) => {
    const dateStr = toLocalDateString(date);
    return events.filter(event => {
      const eventStart = (event.startDate && String(event.startDate).substring(0, 10)) || '';
      const eventEnd = (event.endDate && String(event.endDate).substring(0, 10)) || '';
      if (!eventStart || !eventEnd) return false;
      return dateStr >= eventStart && dateStr <= eventEnd;
    });
  };

  const getTimeSlots = () => {
    const slots = [];
    for (let h = 0; h < 24; h++) {
      slots.push(`${String(h).padStart(2, '0')}:00`);
    }
    return slots;
  };

  const formatDateRange = () => {
    const locale = navigator.language || 'fr-FR';
    if (viewMode === 'week') {
      const days = getDaysInWeek();
      const start = days[0];
      const end = days[6];
      return `${start.toLocaleDateString(locale, { day: 'numeric', month: 'long' })} - ${end.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
  };

  const formatDayName = (date) => {
    return date.toLocaleDateString(navigator.language || 'fr-FR', { weekday: 'short' });
  };

  const formatDayNumber = (date) => {
    return date.getDate();
  };

  const isTodayDate = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  
  
  const getEventPosition = (event, date) => {
    if (event.isAllDay) return { top: 0, height: 30 };
    
    const start = new Date(`${(event.startDate && String(event.startDate).substring(0, 10)) || ''}T${event.startTime || '00:00'}`);
    const end = new Date(`${(event.endDate && String(event.endDate).substring(0, 10)) || ''}T${event.endTime || '23:59'}`);
    const dayStr = toLocalDateString(date);
    const eventStartStr = (event.startDate && String(event.startDate).substring(0, 10)) || '';
    
    if (eventStartStr && dayStr === eventStartStr) {
      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const endMinutes = end.getHours() * 60 + end.getMinutes();
      const duration = endMinutes - startMinutes;
      
      return {
        top: (startMinutes / 60) * 60,
        height: Math.max(30, (duration / 60) * 60)
      };
    }
    return null;
  };
  
  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Calendar header - inside body (scrolls with content), not fixed top bar */}
      <div style={{
        padding: isMobile 
          ? 'calc(16px + env(safe-area-inset-top, 0)) 16px 20px' 
          : '24px 20px 20px',
        background: 'transparent',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'flex-start',
          gap: isMobile ? '12px' : '16px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '12px' : '16px',
            flex: 1,
            minWidth: 0
          }}>
            {onNavigate && (
              <button
                onClick={() => onNavigate('home')}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '10px',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  backdropFilter: 'blur(10px)',
                  flexShrink: 0,
                  width: '44px',
                  height: '44px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title={t('common.back_to_home')}
              >
                <Home size={20} />
              </button>
            )}
            <div style={{
              fontSize: isMobile ? '1.25rem' : 'clamp(28px, 5vw, 40px)',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              lineHeight: 1.2,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flex: 1,
              minWidth: 0,
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
              wordBreak: 'normal'
            }}>
              <CalendarIcon size={isMobile ? 24 : 32} style={{ flexShrink: 0 }} />
              <span style={{ minWidth: 0 }}>{t('calendar.title')}</span>
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            flexShrink: 0,
            ...(isMobile && { width: '100%', justifyContent: 'flex-start' })
          }}>
              <button
                onClick={() => navigateDate(-1)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '8px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <ChevronLeft size={18} />
              </button>
              
              <button
                onClick={goToToday}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t('calendar.today')}
              </button>
              
              <button
                onClick={() => navigateDate(1)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '8px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div style={{
              fontSize: 'clamp(14px, 2vw, 16px)',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.8)',
              minWidth: isMobile ? '100%' : 'auto'
            }}>
              {formatDateRange()}
            </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setViewMode('week')}
              style={{
                background: viewMode === 'week' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: viewMode === 'week' ? 600 : 400,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (viewMode !== 'week') {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (viewMode !== 'week') {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }
              }}
            >
              {t('calendar.week')}
            </button>
            <button
              onClick={() => setViewMode('month')}
              style={{
                background: viewMode === 'month' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: viewMode === 'month' ? 600 : 400,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (viewMode !== 'month') {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (viewMode !== 'month') {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }
              }}
            >
              {t('calendar.month')}
            </button>
            
            <button
              onClick={() => openEventModal()}
              style={{
                background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                border: 'none',
                color: '#ffffff',
                padding: '10px 20px',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Plus size={18} />
              {t('calendar.new')}
            </button>
          </div>
        </div>
      </div>

      {/* Contenu principal - header is above, gap between header and calendar grid */}
      <div className="calendar-content" style={{
        paddingTop: isMobile ? '0' : '0',
        paddingBottom: '40px',
        paddingLeft: isMobile ? '16px' : 'clamp(16px, 4vw, 40px)',
        paddingRight: isMobile ? '16px' : 'clamp(16px, 4vw, 40px)',
        marginTop: isMobile ? '24px' : '28px',
        maxWidth: '1200px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px'
          }}>
            {t('calendar.loading')}
          </div>
        ) : viewMode === 'week' ? (
          <WeekView
            days={getDaysInWeek()}
            events={events}
            onEventClick={(event) => openEventModal(null, null, event)}
            onTimeSlotClick={(date, time) => openEventModal(date, time)}
            isMobile={isMobile}
            t={t}
          />
        ) : (
          <MonthView
            days={getDaysInMonth()}
            events={events}
            onEventClick={(event) => openEventModal(null, null, event)}
            onDayClick={(date) => {
              setViewMode('week');
              setCurrentDate(date);
            }}
            isMobile={isMobile}
            currentDate={currentDate}
            t={t}
          />
        )}
      </div>

      {/* Modal événement */}
      {showEventModal && (
        <EventModal
          eventForm={eventForm}
          setEventForm={setEventForm}
          onSave={saveEvent}
          onClose={closeEventModal}
          onDelete={selectedEvent ? () => deleteEvent(selectedEvent.id) : null}
          isEdit={!!selectedEvent}
          isMobile={isMobile}
          t={t}
        />
      )}

      {/* CSS pour animations et responsive */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .calendar-content {
            padding: 0 40px 40px !important;
            margin-top: 28px !important;
          }
        }
        @media (min-width: 1024px) {
          .calendar-content {
            padding: 0 60px 60px !important;
          }
        }
        .calendar-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

// Vue Semaine
const WeekView = ({ days, events, onEventClick, onTimeSlotClick, isMobile, t }) => {
  const timeSlots = Array.from({ length: 24 }, (_, i) => i);

  const formatDayName = (date) => {
    return date.toLocaleDateString(navigator.language || 'fr-FR', { weekday: 'short' });
  };

  const formatDayNumber = (date) => {
    return date.getDate();
  };

  // On mobile: min width for grid so all 7 days + time column fit, enables horizontal scroll
  const gridMinWidth = isMobile ? 700 : 'auto';

  const isTodayDate = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const gridEl = (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '60px repeat(7, 1fr)',
      gap: '1px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      overflow: 'hidden',
      minWidth: gridMinWidth
    }}>
      {/* En-tête jours */}
      <div></div>
      {days.map((day, idx) => (
        <div
          key={idx}
          style={{
            background: 'rgba(15, 23, 42, 0.8)',
            padding: '12px',
            textAlign: 'center',
            borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}>
            {formatDayName(day)}
          </div>
          <div style={{
            fontSize: '18px',
            fontWeight: 600,
            color: isTodayDate(day) ? '#3B82F6' : '#ffffff'
          }}>
            {formatDayNumber(day)}
          </div>
        </div>
      ))}

      {/* Ligne "Journée entière" en haut : une colonne par jour */}
      <div style={{
        background: 'rgba(15, 23, 42, 0.9)',
        padding: '6px 8px',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.7)',
        textAlign: 'right',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {t('calendar.all_day')}
      </div>
      {days.map((day, idx) => {
        const dayStr = toLocalDateString(day);
        const allDayEvents = events.filter(event => {
          if (!event.isAllDay) return false;
          const eventStart = (event.startDate && String(event.startDate).substring(0, 10)) || '';
          const eventEnd = (event.endDate && String(event.endDate).substring(0, 10)) || '';
          if (!eventStart || !eventEnd) return false;
          return dayStr >= eventStart && dayStr <= eventEnd;
        });
        return (
          <div
            key={`allday-${idx}`}
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              minHeight: '44px',
              padding: '6px',
              borderRight: '1px solid rgba(255, 255, 255, 0.05)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              overflowY: 'auto'
            }}
          >
            {allDayEvents.map((event) => (
              <div
                key={`allday-${idx}-${event.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventClick(event);
                }}
                style={{
                  background: event.color || '#3B82F6',
                  color: '#ffffff',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={event.title}
              >
                {event.title}
              </div>
            ))}
          </div>
        );
      })}

      {/* Créneaux horaires */}
      {timeSlots.map((hour) => (
        <React.Fragment key={hour}>
          <div style={{
            background: 'rgba(15, 23, 42, 0.8)',
            padding: '4px 8px',
            fontSize: '12px',
            color: 'rgba(255, 255, 255, 0.6)',
            textAlign: 'right',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            {String(hour).padStart(2, '0')}:00
          </div>
          {days.map((day, dayIdx) => {
            const dayDateStr = toLocalDateString(day);
            const dayEvents = events.filter(event => {
              if (event.isAllDay) return false;
              const eventStartStr = (event.startDate && String(event.startDate).substring(0, 10)) || '';
              const eventEndStr = (event.endDate && String(event.endDate).substring(0, 10)) || '';
              if (!eventStartStr || !eventEndStr) return false;
              const dayInRange = dayDateStr >= eventStartStr && dayDateStr <= eventEndStr;
              if (!dayInRange) return false;
              const timeStr = (event.startTime && String(event.startTime).trim()) || '00:00';
              const timePart = timeStr.includes(' ') ? timeStr.split(' ')[1] || timeStr : timeStr;
              const eventHour = parseInt(timePart.split(':')[0], 10);
              const hourNum = isNaN(eventHour) ? 0 : eventHour;
              const isStartDay = dayDateStr === eventStartStr;
              if (isStartDay) return hourNum === hour;
              return hour === 0;
            });

            return (
              <div
                key={`${hour}-${dayIdx}`}
                onClick={() => onTimeSlotClick(toLocalDateString(day), `${String(hour).padStart(2, '0')}:00`)}
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  minHeight: '60px',
                  borderRight: '1px solid rgba(255, 255, 255, 0.05)',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  cursor: 'pointer',
                  position: 'relative',
                  padding: '2px',
                  overflowY: 'auto'
                }}
              >
                {dayEvents.map((event) => (
                  <div
                    key={`${event.id}-${dayDateStr}-${hour}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                    style={{
                      background: event.color || '#3B82F6',
                      color: '#ffffff',
                      padding: '4px 6px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginBottom: '2px',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={event.title}
                  >
                    {(event.startTime && String(event.startTime).substring(0, 5)) || '00:00'} - {event.title}
                  </div>
                ))}
              </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );

  // On mobile: wrap in horizontal scroll container
  if (isMobile) {
    return (
      <div style={{
        overflowX: 'auto',
        overflowY: 'visible',
        marginTop: '20px',
        paddingBottom: '8px',
        WebkitOverflowScrolling: 'touch',
        borderRadius: '8px'
      }}>
        {gridEl}
      </div>
    );
  }

  return (
    <div style={{ marginTop: '20px' }}>
      {gridEl}
    </div>
  );
};

// Vue Mois
const MonthView = ({ days, events, onEventClick, onDayClick, isMobile, currentDate, t }) => {
  // Générer les jours de la semaine localisés
  const getWeekDays = () => {
    const locale = navigator.language || 'fr-FR';
    const baseDate = new Date(2024, 0, 7); // Dimanche 7 janvier 2024
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);
      weekDays.push(date.toLocaleDateString(locale, { weekday: 'short' }));
    }
    return weekDays;
  };
  const weekDays = getWeekDays();

  const getEventsForDate = (date) => {
    const dateStr = toLocalDateString(date);
    return events.filter(event => {
      const eventStart = (event.startDate && String(event.startDate).substring(0, 10)) || '';
      const eventEnd = (event.endDate && String(event.endDate).substring(0, 10)) || '';
      if (!eventStart || !eventEnd) return false;
      return dateStr >= eventStart && dateStr <= eventEnd;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatDayNumber = (date) => {
    return date.getDate();
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '1px',
      background: 'rgba(255, 255, 255, 0.1)',
      marginTop: '20px',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      {/* En-tête jours de la semaine */}
      {weekDays.map((day) => (
        <div
          key={day}
          style={{
            background: 'rgba(15, 23, 42, 0.8)',
            padding: '12px',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '14px'
          }}
        >
          {day}
        </div>
      ))}

      {/* Jours du mois */}
      {days.map((day, idx) => {
        const dayEvents = getEventsForDate(day);
        const isCurrentMonth = day.getMonth() === currentDate.getMonth();
        const isTodayDate = isToday(day);

        return (
          <div
            key={idx}
            onClick={() => onDayClick(day)}
            style={{
              background: isCurrentMonth ? 'rgba(30, 41, 59, 0.5)' : 'rgba(15, 23, 42, 0.3)',
              minHeight: '120px',
              padding: '8px',
              cursor: 'pointer',
              border: isTodayDate ? '2px solid #3B82F6' : '1px solid rgba(255, 255, 255, 0.05)',
              position: 'relative'
            }}
          >
            <div style={{
              fontSize: '14px',
              fontWeight: isTodayDate ? 700 : 500,
              color: isTodayDate ? '#3B82F6' : (isCurrentMonth ? '#ffffff' : 'rgba(255, 255, 255, 0.4)'),
              marginBottom: '4px'
            }}>
              {formatDayNumber(day)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {dayEvents.slice(0, 3).map((event) => (
                <div
                  key={event.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEventClick(event);
                  }}
                  style={{
                    background: event.color || '#3B82F6',
                    color: '#ffffff',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer'
                  }}
                  title={event.title}
                >
                  {event.isAllDay ? '🌅' : event.startTime} {event.title}
                </div>
              ))}
              {dayEvents.length > 3 && (
                <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {t('calendar.more_events').replace('{{count}}', dayEvents.length - 3)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Modal événement
const EventModal = ({ eventForm, setEventForm, onSave, onClose, onDelete, isEdit, isMobile, t }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: isMobile ? '20px' : '40px'
    }}
    onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1e293b',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
            {isEdit ? t('calendar.edit_event') : t('calendar.new_event')}
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {isEdit && onDelete && (
              <button
                onClick={onDelete}
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Trash2 size={16} />
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#ffffff',
                padding: '8px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Formulaire */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
              {t('calendar.title_field')}
            </label>
            <input
              type="text"
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              placeholder={t('calendar.title_placeholder')}
              style={{
                width: '100%',
                padding: '10px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                color: '#ffffff',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
              <input
                type="checkbox"
                checked={eventForm.isAllDay}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setEventForm({
                    ...eventForm,
                    isAllDay: checked,
                    startTime: checked ? '00:00' : eventForm.startTime,
                    endTime: checked ? '23:59' : eventForm.endTime
                  });
                }}
                style={{ margin: 0 }}
              />
              {t('calendar.all_day')}
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
                {t('calendar.start_date')}
              </label>
              <input
                type="date"
                value={eventForm.startDate}
                onChange={(e) => setEventForm({ ...eventForm, startDate: e.target.value, endDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
                {t('calendar.start_time')}
              </label>
              <input
                type="time"
                value={eventForm.startTime || '00:00'}
                onChange={(e) => setEventForm({ ...eventForm, startTime: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  opacity: eventForm.isAllDay ? 0.8 : 1
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
                {t('calendar.end_date')}
              </label>
              <input
                type="date"
                value={eventForm.endDate}
                onChange={(e) => setEventForm({ ...eventForm, endDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
                {t('calendar.end_time')}
              </label>
              <input
                type="time"
                value={eventForm.endTime || '23:59'}
                onChange={(e) => setEventForm({ ...eventForm, endTime: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '14px',
                  opacity: eventForm.isAllDay ? 0.8 : 1
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
              <MapPin size={14} style={{ display: 'inline', marginRight: '6px' }} />
              {t('calendar.location')}
            </label>
            <input
              type="text"
              value={eventForm.location}
              onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
              placeholder={t('calendar.location_placeholder')}
              style={{
                width: '100%',
                padding: '10px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                color: '#ffffff',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
              {t('calendar.description')}
            </label>
            <textarea
              value={eventForm.description}
              onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
              placeholder={t('calendar.description_placeholder')}
              rows={4}
              style={{
                width: '100%',
                padding: '10px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                color: '#ffffff',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>
              {t('calendar.color')}
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'].map((color) => (
                <button
                  key={color}
                  onClick={() => setEventForm({ ...eventForm, color })}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    border: eventForm.color === color ? '3px solid #ffffff' : '2px solid rgba(255, 255, 255, 0.2)',
                    background: color,
                    cursor: 'pointer'
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              {t('calendar.cancel')}
            </button>
            <button
              onClick={onSave}
              disabled={!eventForm.title}
              style={{
                background: eventForm.title ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: '#ffffff',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: eventForm.title ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Save size={16} />
              {t('calendar.save')}
            </button>
        </div>
      </div>
    </div>
  );
};


export default PersonalCalendar;

